import {
  PermissionCode,
  REMOTE_SESSION_STATUS_LABELS,
  RemoteSessionStatus,
  type RemoteSession,
  type TicketDetail,
} from '@gb-ticket/shared-types';
import { Loader2Icon, MonitorSmartphoneIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { hasPermission } from '@/features/auth/permissions';
import { useMe } from '@/features/auth/use-me';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import {
  connectLaunchErrorMessage,
  isGbTicketRemoteProtocolUrl,
  useConnectLaunch,
  useCurrentRemoteSession,
  useEndRemoteSession,
  useMyConnectorDevices,
  useRequestRemoteSession,
} from '@/products/helpdesk/features/remote-support/use-remote-support';
import {
  TicketSectionCard,
  type TicketSectionTone,
} from '@/products/helpdesk/features/tickets/ticket-section-card';
import { ApiRequestError } from '@/lib/api';
import { useVoice, useVoiceCopy } from '@/platform/voice/use-voice';
import { formatCountdown, formatDateTime } from '@/lib/format';

function tone(status: RemoteSession['status'] | undefined): TicketSectionTone {
  if (status === RemoteSessionStatus.ACTIVE) return 'emerald';
  if (status === RemoteSessionStatus.REQUESTED) return 'amber';
  return 'muted';
}

const OPEN_TICKET_STATUSES = new Set(['NEW', 'IN_PROGRESS', 'WAITING_FOR_CUSTOMER', 'REOPENED']);

/**
 * Remote-support panel in the ticket sidebar.
 *
 * Requester: start a session and confirm on the PC. Assigned agent: one click
 * “Mit RustDesk verbinden” — the browser never sees ID or password.
 */
export function TicketRemoteSupportPanel({ ticket }: { ticket: TicketDetail }) {
  const ref = String(ticket.number);
  const me = useMe();
  const voice = useVoice();
  const copy = useVoiceCopy();
  const paths = useAppPaths();
  const isRequester = me.data?.id === ticket.requester.id;
  const isStaff = hasPermission(me.data, PermissionCode.TICKETS_READ_ALL);
  const canRequest = isRequester && hasPermission(me.data, PermissionCode.REMOTE_SUPPORT_REQUEST);
  const canConnect = hasPermission(me.data, PermissionCode.REMOTE_SUPPORT_CONNECT);
  const relevant =
    isRequester ||
    isStaff ||
    canConnect ||
    hasPermission(me.data, PermissionCode.REMOTE_SUPPORT_MANAGE);

  const query = useCurrentRemoteSession(ref, relevant);
  const request = useRequestRemoteSession(ref);
  const end = useEndRemoteSession(ref);
  const launch = useConnectLaunch(ref);
  const session = query.data?.session ?? null;
  const live =
    session?.status === RemoteSessionStatus.REQUESTED ||
    session?.status === RemoteSessionStatus.ACTIVE;
  const myDevices = useMyConnectorDevices(canConnect && live);
  const [protocolMissed, setProtocolMissed] = useState(false);

  const agentConnectorPaired =
    (myDevices.data?.items.filter((item) => item.isActive).length ?? 0) > 0;

  if (!relevant) {
    return null;
  }

  const ticketOpen = OPEN_TICKET_STATUSES.has(ticket.status);
  const connectorAvailable = query.data?.connectorAvailable ?? false;

  async function onRequest() {
    try {
      await request.mutateAsync();
      toast.success(copy('helpdesk.remote.requested'));
    } catch (error) {
      toast.error(error instanceof ApiRequestError ? error.message : 'Anfrage fehlgeschlagen.');
    }
  }

  async function onEnd() {
    if (!session) return;
    try {
      await end.mutateAsync(session.id);
      toast.success('Sitzung beendet.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Sitzung konnte nicht beendet werden.',
      );
    }
  }

  async function onConnect() {
    if (!session) return;
    setProtocolMissed(false);
    try {
      const result = await launch.mutateAsync(session.id);
      if (!isGbTicketRemoteProtocolUrl(result.protocolUrl)) {
        toast.error('Ungültige Startadresse vom Server.');
        return;
      }
      toast.success('Connector startet RustDesk …');
      window.location.assign(result.protocolUrl);
      window.setTimeout(() => setProtocolMissed(true), 2500);
    } catch (error) {
      toast.error(connectLaunchErrorMessage(error, voice));
    }
  }

  return (
    <TicketSectionCard
      tone={tone(session?.status)}
      icon={MonitorSmartphoneIcon}
      collapsible
      defaultOpen={live}
      key={live ? 'live' : 'idle'}
      title="Remote-Support"
      description={
        session
          ? `${REMOTE_SESSION_STATUS_LABELS[session.status]}${session.device ? ` · ${session.device.hostname}` : ''}`
          : isRequester
            ? copy('helpdesk.remote.consent')
            : 'Nach Zustimmung: mit dem Mitarbeiterportal Connector verbinden'
      }
    >
      <div className="space-y-3 text-sm">
        {query.isPending && (
          <p className="flex items-center gap-2 text-muted-foreground">
            <Loader2Icon className="size-4 animate-spin" aria-hidden /> Wird geladen …
          </p>
        )}

        {session && (
          <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1">
            <dt className="text-muted-foreground">Angefragt</dt>
            <dd>{formatDateTime(session.requestedAt)}</dd>
            {session.status === RemoteSessionStatus.ACTIVE && (
              <>
                <dt className="text-muted-foreground">Gültig bis</dt>
                <dd>{formatDateTime(session.expiresAt)}</dd>
                <dt className="text-muted-foreground">Restlaufzeit</dt>
                <dd>
                  <SessionRemainingTime expiresAt={session.expiresAt} />
                </dd>
              </>
            )}
            {session.status === RemoteSessionStatus.REQUESTED && (
              <>
                <dt className="text-muted-foreground">Zustimmung bis</dt>
                <dd>{formatDateTime(session.expiresAt)}</dd>
              </>
            )}
            {session.retrievalCount > 0 && (
              <>
                <dt className="text-muted-foreground">Verbindungen</dt>
                <dd>{session.retrievalCount}</dd>
              </>
            )}
          </dl>
        )}

        {isRequester && !canRequest && (
          <p className="text-muted-foreground">
            Fernwartung ist für Ihr Konto noch nicht aktiv. Bitte laden Sie die Seite neu oder
            melden Sie sich einmal ab und wieder an.
          </p>
        )}

        {!session && !canRequest && (isStaff || canConnect) && (
          <ConnectorHelp detailsLabel="Wie startet der Anfragende die Fernwartung?">
            Der Anfragende startet die Fernwartung selbst: Mitarbeiterportal Connector unter
            Einstellungen mit einem Kopplungscode koppeln (der Code gehört nie in RustDesk), Ticket
            öffnen, „Remote-Support starten“, Dialog am Rechner mit „Zulassen“ bestätigen. Danach
            klickt der zugewiesene Agent „Mit RustDesk verbinden“.
          </ConnectorHelp>
        )}

        {canRequest && !live && (
          <div className="space-y-2">
            {!connectorAvailable && (
              <div className="space-y-2">
                <p className="text-muted-foreground">
                  Connector noch nicht gekoppelt.{' '}
                  <Link
                    to={paths.settings}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Einstellungen
                  </Link>
                </p>
                <ConnectorHelp detailsLabel="Wie koppelt man den Connector?">
                  Das ist ein Windows-Programm der IT (Taskleiste, nicht RustDesk). Installation und
                  Kopplung stehen unter Einstellungen. Den Kopplungscode nur in den Connector
                  eingeben, nie in RustDesk.
                </ConnectorHelp>
              </div>
            )}
            {!ticketOpen && (
              <p className="text-muted-foreground">
                Für geschlossene Tickets ist keine Fernwartung möglich.
              </p>
            )}
            <Button
              size="sm"
              onClick={() => void onRequest()}
              disabled={request.isPending || !connectorAvailable || !ticketOpen}
            >
              {request.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Remote-Support starten
            </Button>
            <ConnectorHelp detailsLabel="Was passiert nach dem Start?">
              Auf Ihrem Rechner erscheint ein Dialog. Erst nach „Zulassen“ kann der zugewiesene
              Agent über seinen eigenen Connector „Mit RustDesk verbinden“. Kopplungscode,
              Geräte-GUID und RustDesk-ID gehören nicht in dieses Ticket.
            </ConnectorHelp>
          </div>
        )}

        {session?.status === RemoteSessionStatus.REQUESTED && (
          <p className="text-muted-foreground">
            {isRequester
              ? copy('helpdesk.remote.confirm-dialog')
              : `${session.requestedBy.displayName} muss die Anfrage am Rechner bestätigen.`}
          </p>
        )}

        {session?.status === RemoteSessionStatus.ACTIVE && canConnect && !session.canConnect && (
          <p className="text-muted-foreground">
            Verbinden darf nur der Agent, dem das Ticket aktuell zugewiesen ist.
          </p>
        )}
        {session?.canConnect && (
          <div className="space-y-2">
            {!agentConnectorPaired && !protocolMissed && (
              <div className="space-y-2">
                <p className="text-muted-foreground">
                  Connector noch nicht gekoppelt.{' '}
                  <Link
                    to={paths.settings}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    Einstellungen
                  </Link>
                </p>
                <ConnectorHelp detailsLabel="Wie koppelt man den Connector?">
                  Bitte unter Einstellungen einen Kopplungscode erzeugen und den Connector auf Ihrem
                  PC koppeln. Den Code nur in den Mitarbeiterportal Connector eingeben, nie in
                  RustDesk.
                </ConnectorHelp>
              </div>
            )}
            {protocolMissed && (
              <p className="text-muted-foreground">
                Mitarbeiterportal Connector auf diesem PC koppeln —{' '}
                <Link
                  to={paths.settings}
                  className="font-medium text-foreground underline-offset-4 hover:underline"
                >
                  Einstellungen
                </Link>
                .
              </p>
            )}
            <Button size="sm" onClick={() => void onConnect()} disabled={launch.isPending}>
              {launch.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
              Mit RustDesk verbinden
            </Button>
          </div>
        )}

        {session?.canEnd && (
          <Button variant="outline" size="sm" onClick={() => void onEnd()} disabled={end.isPending}>
            {end.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            Sitzung beenden
          </Button>
        )}
      </div>
    </TicketSectionCard>
  );
}

function ConnectorHelp({ detailsLabel, children }: { detailsLabel: string; children: string }) {
  return (
    <details className="text-sm text-muted-foreground">
      <summary className="cursor-pointer font-medium text-foreground">{detailsLabel}</summary>
      <p className="mt-2 leading-relaxed">{children}</p>
    </details>
  );
}

function SessionRemainingTime({ expiresAt }: { expiresAt: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  const remaining = new Date(expiresAt).getTime() - now;
  if (Number.isNaN(remaining)) {
    return '—';
  }
  return remaining <= 0 ? 'abgelaufen' : formatCountdown(remaining);
}
